import React, { useEffect, useState } from 'react';
import { Upload as UploadIcon, Info } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { mccGet } from './mccApi';
import MccUploader from './MccUploader';
import { PLATFORMS } from './mccConstants';

/**
 * MccInlineFootageUploader — a compact "pick project, drop file" panel.
 * Reused on Raw Footage, Filming Requests and Editing Queue for consistency.
 *
 * Props:
 *   mode          'raw' (raw video/audio/image → /marketing/uploads)
 *                 'edited' (edited_video against a project → /marketing/versions)
 *   title         Panel heading (e.g. "Upload raw footage from a shoot")
 *   hint          Sub-heading text
 *   defaultKind   Initial kind selection for 'raw' mode
 *   onNav         Optional navigation callback to jump to Ideas etc.
 *   onUploaded    Callback fired after each successful upload
 *   defaultProjectId  Pre-select a specific project
 *
 * Note: the panel supplies its own dark cinematic styling so it feels
 * consistent no matter where it is dropped.
 */
export default function MccInlineFootageUploader({
  mode = 'raw',
  title,
  hint,
  defaultKind = 'raw_video',
  defaultProjectId = '',
  onNav,
  onUploaded,
}) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(defaultProjectId || '');
  const [kind, setKind] = useState(defaultKind);
  const [platform, setPlatform] = useState('');
  const [label, setLabel] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await mccGet('/marketing/projects');
        setProjects(res.items || []);
      } catch { /* silent */ }
    })();
  }, []);

  const isEdited = mode === 'edited';
  const canUpload = !isEdited || !!projectId; // edited versions require a project

  const endpoint = isEdited ? '/marketing/versions' : '/marketing/uploads';
  const extraFields = isEdited
    ? { project_id: projectId, platform: platform || '', kind: 'edited_video', label }
    : { project_id: projectId, kind };

  const accept =
    isEdited ? 'video/*,image/*'
      : kind === 'raw_video' ? 'video/*'
        : kind === 'raw_audio' ? 'audio/*'
          : kind === 'raw_image' ? 'image/*' : '';

  return (
    <div
      className="rounded-2xl border border-slate-800 bg-slate-950 text-slate-100 p-4"
      data-testid="mcc-inline-uploader"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center">
          <UploadIcon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-sm font-bold text-white">{title}</p>
          <p className="text-[11px] text-slate-400">{hint}</p>
        </div>
      </div>

      <div className={`grid ${isEdited ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-3 mb-3`}>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Project {isEdited && <span className="text-rose-400">*</span>}</p>
          <Select
            value={projectId || (isEdited ? '' : 'unassigned')}
            onValueChange={(v) => setProjectId(v === 'unassigned' ? '' : v)}
          >
            <SelectTrigger className="h-9 bg-white text-slate-900" data-testid="mcc-inline-project">
              <SelectValue placeholder="Select project…" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {!isEdited && <SelectItem value="unassigned">Unassigned (link later)</SelectItem>}
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!isEdited && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">File kind</p>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-9 bg-white text-slate-900" data-testid="mcc-inline-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="raw_video">Raw video</SelectItem>
                <SelectItem value="raw_audio">Raw audio</SelectItem>
                <SelectItem value="raw_image">Raw image</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {isEdited && (
          <>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Platform (optional)</p>
              <Select value={platform || 'master'} onValueChange={(v) => setPlatform(v === 'master' ? '' : v)}>
                <SelectTrigger className="h-9 bg-white text-slate-900" data-testid="mcc-inline-platform">
                  <SelectValue placeholder="Platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="master">Master (all)</SelectItem>
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Version label</p>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. V1 rough cut"
                className="h-9 bg-white text-slate-900 border-slate-200"
              />
            </div>
          </>
        )}
      </div>

      {isEdited && !projectId ? (
        <div className="rounded-lg border border-dashed border-slate-700 p-4 text-center text-[12px] text-slate-400">
          Pick a project first — edited versions must be attached to an existing project.
        </div>
      ) : (
        <MccUploader
          endpoint={endpoint}
          extraFields={extraFields}
          accept={accept}
          onUploaded={onUploaded}
          multiple={!isEdited}
          dark
          compact
          label={isEdited
            ? 'Drop your edited version here'
            : `Drop ${kind.replace(/_/g, ' ')} here`}
          sub={isEdited
            ? 'MP4, MOV, WebM. Editing notes can be added inside the project drawer.'
            : 'Uploads store to Emergent object storage. Files appear immediately below.'}
          testid="mcc-inline-drop"
        />
      )}

      {projects.length === 0 && !isEdited && (
        <div className="mt-3 flex items-center gap-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          <Info className="w-3.5 h-3.5 flex-shrink-0" />
          <span>No projects yet. Upload as &ldquo;Unassigned&rdquo; and link later — or</span>
          <button
            onClick={() => onNav && onNav('ideas')}
            className="underline font-semibold hover:text-amber-200"
          >create your first project from an Idea</button>
        </div>
      )}
    </div>
  );
}
